import React, { useState, useEffect, useCallback } from 'react';
import '../styles/App.css';
import '../styles/Orb.css';
import '../styles/landing.css';
import CourseHistory from '../components/CourseHistory';
import AccountPanel from '../components/AccountPanel';
import SentientOrb from '../orb/SentientOrb.tsx';
import { createCourseFromTemplateById, getFeaturedCourses } from '../context/engine.ts';
import packageJson from '../../package.json';
import { getFeaturedCoursesForWeek, getCategoryColor, inferCategoryFromName } from '../utils/categoryMapper';
import {
    FaCalculator,
    FaAtom,
    FaFlask,
    FaDna,
    FaLandmark,
    FaBook,
    FaChartLine,
    FaBrain,
    FaLightbulb,
    FaLaptopCode,
    FaPalette,
    FaUsers,
    FaMoon,
    FaSun,
    FaBars,
    FaPlus
} from 'react-icons/fa';

// Subject data array with colors
const subjects = [
    { name: 'Mathematics', icon: FaCalculator, color: '#F4D03F' },
    { name: 'Physics', icon: FaAtom, color: '#5DADE2' },
    { name: 'Chemistry', icon: FaFlask, color: '#58D68D' },
    { name: 'Biology', icon: FaDna, color: '#F8B739' },
    { name: 'History', icon: FaLandmark, color: '#5B7AE2' },
    { name: 'Literature', icon: FaBook, color: '#AF7AC5' },
    { name: 'Economics', icon: FaChartLine, color: '#5D6D7E' },
    { name: 'Psychology', icon: FaBrain, color: '#EC7063' },
    { name: 'Philosophy', icon: FaLightbulb, color: '#48C9B0' },
    { name: 'Computer Science', icon: FaLaptopCode, color: '#F4D03F' },
    { name: 'Art History', icon: FaPalette, color: '#E59866' },
    { name: 'Sociology', icon: FaUsers, color: '#5DADE2' },
    { name: 'Environmental Science', icon: FaFlask, color: '#52BE80' }
];

const DESKTOP_ANNOUNCEMENT =
    'TeachingOrb desktop application is on its way! Learn your way, locally and offline from open source models! With a ton of new features at an even better price point!';

// Academic topics and news for typing animation
const academicTopics = [
    "Quantum Physics",
    "Machine Learning",
    "Project 2025",
    "Climate Change",
    "Molecular Biology",
    "World Literature",
    "Economic Trends",
    "Human Psychology",
    "Philosophy & Ethics",
    "Data Science",
    "Modern Art",
    "Global Politics"
];

function Landing({
    onNavigateToSignIn,
    onNavigateToSignUp,
    currentUser = null,
    isAuthenticated = false,
    profileLoaded = false,
    onCourseSelect,
    theme,
    onToggleTheme,
    onSignOut,
    onNavigateToInquiry,
    onNavigateToCourseGallery,
    onNavigateToTermsOfUse,
    onNavigateToPrivacyPolicy,
    onNavigateToUpgrade
}) {
    // TYPING ANIMATION DISABLED
    // const [currentTopicIndex, setCurrentTopicIndex] = useState(0);
    // const [displayedText, setDisplayedText] = useState('');
    // const [isTyping, setIsTyping] = useState(true);
    // const [charIndex, setCharIndex] = useState(0);
    const [creatingCourse, setCreatingCourse] = useState(false);
    const [accountPanelState, setAccountPanelState] = useState('hidden'); // 'hidden', 'visible'
    const [courseHistoryState, setCourseHistoryState] = useState('hidden'); // 'hidden', 'entering', 'visible', 'exiting'
    const [courseHistoryLoaded, setCourseHistoryLoaded] = useState(false); // Track if course history has finished loading
    const [featuredCourses, setFeaturedCourses] = useState([]);
    const [loadingFeaturedCourses, setLoadingFeaturedCourses] = useState(true);

    // For non-authenticated users, mark course history as loaded immediately
    useEffect(() => {
        if (!isAuthenticated) {
            setCourseHistoryLoaded(true);
        } else {
            setCourseHistoryLoaded(false);
        }
    }, [isAuthenticated]);

    // Fetch featured courses on mount
    useEffect(() => {
        const loadFeaturedCourses = async () => {
            try {
                setLoadingFeaturedCourses(true);
                const courses = await getFeaturedCourses();
                // Get 8 featured courses for this week using rotation logic
                const weeklyFeatured = getFeaturedCoursesForWeek(courses, 8);
                setFeaturedCourses(weeklyFeatured);
            } catch (error) {
                console.error('Error loading featured courses:', error);
                setFeaturedCourses([]);
            } finally {
                setLoadingFeaturedCourses(false);
            }
        };

        loadFeaturedCourses();
    }, []);

    // Memoized callback to prevent re-renders during typing animation
    const handleCourseHistoryLoadingComplete = useCallback(() => {
        setCourseHistoryLoaded(true);
    }, []);

    // Toggle course history panel
    const toggleCourseHistory = () => {
        if (!isAuthenticated) return; // Don't allow toggling if not authenticated
        
        if (courseHistoryState === 'hidden') {
            setCourseHistoryState('entering');
            setTimeout(() => setCourseHistoryState('visible'), 10);
        } else if (courseHistoryState === 'visible') {
            setCourseHistoryState('exiting');
            setTimeout(() => setCourseHistoryState('hidden'), 300);
        }
    };

    const closeCourseHistory = () => {
        if (courseHistoryState === 'visible') {
            setCourseHistoryState('exiting');
            setTimeout(() => setCourseHistoryState('hidden'), 300);
        }
    };

    // Handle course card clicks
    const handleCourseCardClick = useCallback(async (template) => {
        // If user is not authenticated, redirect to sign in
        if (!isAuthenticated) {
            onNavigateToSignIn();
            return;
        }

        // If user is authenticated, check if they already have this course
        try {
            setCreatingCourse(true);

            // Import getUserCourses to check for existing course
            const { getUserCourses } = await import('../context/engine.ts');
            const userCourses = await getUserCourses(currentUser.id);

            // Check if user already has a course from this template
            const existingCourse = userCourses.find(course => course.template_id === template.id);

            if (existingCourse) {
                // Open the existing course instead of creating a new one
                if (onCourseSelect) {
                    onCourseSelect(existingCourse);
                }
            } else {
                // User doesn't have this course yet, create from template
                const course = await createCourseFromTemplateById(template.id, currentUser.id);

                // Navigate to Learn page with the course
                if (onCourseSelect) {
                    onCourseSelect(course);
                }
            }
        } catch (error) {
            alert('Failed to load course. Please try again.');
        } finally {
            setCreatingCourse(false);
        }
    }, [isAuthenticated, currentUser, onCourseSelect, onNavigateToSignIn]);

    // Toggle account panel
    const handleToggleAccountPanel = () => {
        setAccountPanelState(prev => prev === 'hidden' ? 'visible' : 'hidden');
    };

    // Handle course selection from history and close panel
    const handleCourseSelectFromHistory = useCallback((course) => {
        closeCourseHistory();
        setTimeout(() => {
            onCourseSelect(course);
        }, 300);
    }, [onCourseSelect]);

    // TYPING ANIMATION DISABLED - Commented out to prevent re-render issues
    // // Track if initial data has loaded to prevent typing animation from starting too early
    // const [typingReady, setTypingReady] = useState(false);

    // // Compute dataReady directly instead of using state to avoid re-render issues
    // const dataReady = isAuthenticated ? (profileLoaded && courseHistoryLoaded) : true;

    // useEffect(() => {
    //     // Wait for Landing component to fully mount before starting typing animation
    //     if (dataReady && !typingReady) {
    //         // Add a small delay to ensure all elements are rendered
    //         const timer = setTimeout(() => {
    //             setTypingReady(true);
    //         }, 300); // 300ms delay after data is ready
    //         return () => clearTimeout(timer);
    //     }
    // }, [dataReady, typingReady]);

    // useEffect(() => {
    //     // Don't start typing until component is fully mounted and ready
    //     if (!typingReady) {
    //         return;
    //     }

    //     const currentTopic = academicTopics[currentTopicIndex];

    //     if (isTyping) {
    //         // Typing effect
    //         if (charIndex < currentTopic.length) {
    //             const timeout = setTimeout(() => {
    //                 setDisplayedText(currentTopic.substring(0, charIndex + 1));
    //                 setCharIndex(charIndex + 1);
    //             }, 100); // Typing speed
    //             return () => clearTimeout(timeout);
    //         } else {
    //             // Pause before deleting
    //             const timeout = setTimeout(() => {
    //                 setIsTyping(false);
    //             }, 2000); // Pause duration
    //             return () => clearTimeout(timeout);
    //         }
    //     } else {
    //         // Deleting effect
    //         if (charIndex > 0) {
    //             const timeout = setTimeout(() => {
    //                 setDisplayedText(currentTopic.substring(0, charIndex - 1));
    //                 setCharIndex(charIndex - 1);
    //             }, 50); // Deleting speed
    //             return () => clearTimeout(timeout);
    //         } else {
    //             // Move to next topic
    //             setIsTyping(true);
    //             setCurrentTopicIndex((currentTopicIndex + 1) % academicTopics.length);
    //         }
    //     }
    // }, [charIndex, isTyping, currentTopicIndex, typingReady]);

    return (
        <div className="landing-page">
            {/* Course History Panel - Only render if authenticated */}
            {isAuthenticated && (
                <CourseHistory
                    courseHistoryState={courseHistoryState}
                    onClose={closeCourseHistory}
                    currentUser={currentUser}
                    isAuthenticated={isAuthenticated}
                    profileLoaded={profileLoaded}
                    onCourseSelect={handleCourseSelectFromHistory}
                    currentCourse={null}
                    onLoadingComplete={handleCourseHistoryLoadingComplete}
                />
            )}

            {/* Account Panel - Only render if authenticated */}
            {isAuthenticated && (
                <AccountPanel
                    accountPanelState={accountPanelState}
                    onToggleAccountPanel={handleToggleAccountPanel}
                    currentUser={currentUser}
                    onSignOut={onSignOut}
                    theme={theme}
                    onToggleTheme={onToggleTheme}
                />
            )}

            {/* Loading overlay */}
            {creatingCourse && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                    color: 'white',
                    fontSize: '18px',
                    fontWeight: '500'
                }}>
                    Loading course...
                </div>
            )}
            
            {/* Header */}
            <header className="landing-header">
                <div className="landing-header-content">
                    <div className="landing-logo">
                        {isAuthenticated && (
                            <button 
                                className="menu-icon-btn"
                                onClick={toggleCourseHistory}
                                style={{
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '8px',
                                    marginRight: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '4px',
                                    transition: 'background-color 0.2s ease',
                                    color: 'inherit',
                                    fontSize: '20px'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                                aria-label="Open course history"
                            >
                                <FaBars />
                            </button>
                        )}
                        <img src="/orb_dark.png" alt="TeachingOrb icon" width="20" height="20" />
                        <span style={{ fontFamily: 'Sentient, sans-serif' }}>TeachingOrb</span>
                        <span className="version-badge">v{packageJson.version}</span>
                        {/* Premium New Inquiry Button */}
                        {isAuthenticated && profileLoaded && currentUser?.subscription_type === 'premium' && onNavigateToInquiry && (
                            <button 
                                className="new-inquiry-btn"
                                onClick={onNavigateToInquiry}
                                title="Generate new course"
                                aria-label="Ask Orb"
                            >
                                <FaPlus />
                            </button>
                        )}
                    </div>
                    <div className="landing-header-actions">
                        {/* Theme toggle button - disabled for now */}
                        {/* {onToggleTheme && (
                            <button className="theme-toggle-btn" onClick={onToggleTheme}>
                                {theme === 'light' ? <FaMoon /> : <FaSun />}
                            </button>
                        )} */}
                        {isAuthenticated ? (
                            <div 
                                className="user-avatar" 
                                title={currentUser?.name || currentUser?.email}
                                onClick={handleToggleAccountPanel}
                            >
                                {(currentUser?.name?.[0] || currentUser?.email?.[0] || '?').toUpperCase()}
                            </div>
                        ) : (
                            <button onClick={onNavigateToSignIn} className="btn-get-started">
                                Login / Sign Up
                            </button>
                        )}
                    </div>
                </div>
            </header>

            <div className="landing-announcement" role="region" aria-label="Product announcement">
                <div className="landing-announcement-track">
                    {Array.from({ length: 8 }, (_, index) => (
                        <span
                            key={index}
                            className="landing-announcement-item"
                            aria-hidden={index > 0 ? true : undefined}
                        >
                            <span className="landing-announcement-badge">Coming soon</span>
                            <span>{DESKTOP_ANNOUNCEMENT}</span>
                            <span className="landing-announcement-sep" aria-hidden="true">✦</span>
                        </span>
                    ))}
                </div>
            </div>

            {/* Hero Section */}
            <section className="to-section hero-section">
                <div className="to-section-content">
                    <div className="hero-gradient-box">
                        <div className="hero-content">
                            <h1 className="hero-title">Curriculums Simplified</h1>
                            <p className="hero-subtitle">Transform almost any topic into a crash course with full control over course depth and vocabulary.</p>
                            <div className="hero-buttons">
                                <button
                                    onClick={() => {
                                        if (!isAuthenticated) {
                                            onNavigateToSignUp();
                                        } else if (profileLoaded && currentUser?.subscription_type === 'premium') {
                                            onNavigateToInquiry();
                                        } else if (profileLoaded) {
                                            // Free user - navigate to Stripe checkout
                                            if (onNavigateToUpgrade) {
                                                onNavigateToUpgrade();
                                            }
                                        }
                                        // If profile not loaded, do nothing and wait
                                    }}
                                    className="btn-hero-primary"
                                >
                                    {!isAuthenticated
                                        ? 'Get Started'
                                        : (profileLoaded && currentUser?.subscription_type === 'premium')
                                            ? 'Get Started'
                                            : profileLoaded
                                                ? 'Upgrade Now'
                                                : 'Loading...'}
                                </button>
                                <button
                                    onClick={() => window.location.href = 'https://docs.teachingorb.com'}
                                    className="btn-hero-secondary"
                                >
                                    Learn More
                                </button>
                            </div>
                        </div>
                        <div className="hero-orb">
                            <SentientOrb
                                action="idle"
                                scale={1.2}
                                color="#FFF"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Community Section */}
            <section className="to-section community-section">
                <div className="to-section-content">
                    <p className="community-text">Community Generated Courses</p>
                    
                    {/* Scrolling subject icons */}
                    <div className="community-logos-wrapper">
                        <div className="community-logos-scroll">
                            {/* First set */}
                            {subjects.map((subject, index) => {
                                const Icon = subject.icon;
                                return (
                                    <div key={`subject-${index}`} className="logo-item">
                                        <Icon size={20} />
                                        <span>{subject.name}</span>
                                    </div>
                                );
                            })}
                            {/* Duplicate set for seamless infinite scroll */}
                            {subjects.map((subject, index) => {
                                const Icon = subject.icon;
                                return (
                                    <div key={`subject-duplicate-${index}`} className="logo-item">
                                        <Icon size={20} />
                                        <span>{subject.name}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Featured courses horizontal scroll */}
                    <div className="featured-courses-container">
                        {loadingFeaturedCourses ? (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: '40px 20px',
                                color: '#666',
                                fontSize: '16px',
                                width: '100%'
                            }}>
                                Loading courses...
                            </div>
                        ) : featuredCourses.length === 0 ? (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: '40px 20px',
                                color: '#666',
                                fontSize: '16px',
                                fontStyle: 'italic',
                                width: '100%'
                            }}>
                                Nothing to see here, yet...
                            </div>
                        ) : (
                            <div className="featured-courses-scroll">
                                {featuredCourses.map((template) => {
                                    const category = template.category || inferCategoryFromName(template.original_name);
                                    const color = getCategoryColor(category);

                                    return (
                                        <div
                                            key={template.id}
                                            className="course-card"
                                            style={{
                                                backgroundColor: color,
                                                cursor: 'pointer',
                                                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                                            }}
                                            onClick={() => handleCourseCardClick(template)}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-4px)';
                                                e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = 'none';
                                            }}
                                        >
                                            <div className="course-card-content">
                                                <div className="course-card-header">
                                                    <span className="course-card-category-badge">{category}</span>
                                                    <div className="course-card-icon-circle"></div>
                                                </div>
                                                <h3 className="course-card-title">{template.original_name}</h3>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Link to Course Gallery - outside container, left-aligned */}
                    {!loadingFeaturedCourses && featuredCourses.length > 0 && (
                        <div style={{
                            textAlign: 'left',
                            marginTop: '1.5rem',
                            marginBottom: '1rem'
                        }}>
                            <span
                                onClick={onNavigateToCourseGallery}
                                style={{
                                    color: '#2c2c2c',
                                    fontSize: '1rem',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    textDecoration: 'none',
                                    transition: 'color 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.color = '#5DADE2';
                                    e.currentTarget.style.textDecoration = 'underline';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.color = '#2c2c2c';
                                    e.currentTarget.style.textDecoration = 'none';
                                }}
                            >
                                See our course gallery →
                            </span>
                        </div>
                    )}
                </div>
            </section>

            {/* Footer */}
            <footer className="landing-footer">
                <div className="footer-content">
                    <div className="footer-left">
                        <div className="footer-logo">
                            <img src="/orb_dark.png" alt="TeachingOrb icon" width="16" height="16" />
                            <span style={{ fontFamily: 'Sentient, sans-serif' }}>TeachingOrb</span>
                        </div>
                        <div className="footer-links">
                            <a 
                                onClick={(e) => {
                                    e.preventDefault();
                                    onNavigateToTermsOfUse && onNavigateToTermsOfUse();
                                }}
                                style={{ cursor: 'pointer' }}
                            >
                                Terms of Usage
                            </a>
                            <a 
                                onClick={(e) => {
                                    e.preventDefault();
                                    onNavigateToPrivacyPolicy && onNavigateToPrivacyPolicy();
                                }}
                                style={{ cursor: 'pointer' }}
                            >
                                Privacy
                            </a>
                            <a href="#support">Support</a>
                            <a
                                onClick={(e) => {
                                    e.preventDefault();
                                    window.location.href = 'https://docs.teachingorb.com';
                                }}
                                style={{ cursor: 'pointer' }}
                            >
                                How it works
                            </a>
                        </div>
                    </div>
                    <div className="footer-right">
                        {/* 
                        <a href="https://www.linkedin.com/company/teachingorb/" className="social-icon" title="LinkedIn" target="_blank" rel="noopener noreferrer">
                            <img src="/socials/linkedin.svg" alt="LinkedIn" width="16" height="16" />
                        </a>
                        */}
                   
                        {/* <a href="#" className="social-icon" title="Instagram">
                            <img src="/socials/instagram-icon.svg" alt="Instagram" width="16" height="16" />
                        </a> */}
                        {/* 
                        <a href="https://www.threads.com/@teachingorb.ai?igshid=NTc4MTIwNjQ2YQ==" className="social-icon" title="Threads" target="_blank" rel="noopener noreferrer">
                            <img src="/socials/Threads_light.svg" alt="Threads" width="16" height="16" />
                        </a>
                        */}
                   
                        {/* <a href="#" className="social-icon" title="Substack">
                            <img src="/socials/substack.png" alt="Substack" width="24" height="24" />
                        </a> */}
                    </div>
                </div>
            </footer>
        </div>
    );
}

export default Landing;
